import { fetchScript, fetchScripts, fetchScriptState } from '@/api/apiService';
import dayjs from '@/plugins/dayjs';
import { DateRange, ScriptDownloadSearchForm } from '@/types/components';
import { ChatScript } from '@/types/response';

export default function useChatScripts() {
  const scriptSearchForm = ref<ScriptDownloadSearchForm>({
    chatId: '',
    chatType: 'GENERAL',
    code: '',
    startDate: '',
    endDate: '',
  });
  const scripts = ref<ChatScript[]>([]);
  const isLoading = ref<boolean>(false);
  let pollingIntervalId: ReturnType<typeof setTimeout> | null = null;

  const limitDateRange = computed<DateRange>(() => {
    return {
      start: dayjs().subtract(1, 'year').format('YYYY-MM-DD') + 'T00:00:00',
      end: dayjs().format('YYYY-MM-DD') + 'T23:59:59',
    };
  });

  const pollingScriptString = computed<string>(() => {
    return scripts.value.reduce((acc, { status, chatId, languageCode }) => {
      if (status === 'PROCESSING' && chatId && languageCode) {
        const pair = `${chatId}:${languageCode}`;
        return acc ? `${acc},${pair}` : pair;
      }
      return acc;
    }, '');
  });

  const updateScript = (chatId: string, updatedScript: Partial<ChatScript>) => {
    const inx = scripts.value.findIndex((script) => script.chatId === chatId);
    if (inx !== -1) {
      scripts.value[inx] = { ...scripts.value[inx], ...updatedScript };
    }
  };

  const searchScripts = async (isSingleSearch: boolean) => {
    isLoading.value = true;

    if (isSingleSearch) {
      await requestScript();
    } else {
      await requestScripts();
    }

    isLoading.value = false;
  };

  const requestScript = async () => {
    try {
      const { chatId, chatType, code } = scriptSearchForm.value;
      const res = await fetchScript(chatId, {
        chatType,
        code,
      });
      if (!res.success || !res.data) {
        throw new Error();
      }
      scripts.value = [res.data];
    } catch {
      scripts.value = [];
    }
  };

  const requestScripts = async () => {
    try {
      const { chatType, code, startDate, endDate } = scriptSearchForm.value;
      const res = await fetchScripts({
        chatType,
        ...(code ? { code } : {}),
        ...(startDate ? { startTimestamp: dayjs(startDate).startOf('day').valueOf() } : {}),
        ...(endDate ? { endTimestamp: dayjs(endDate).endOf('day').valueOf() } : {}),
      });
      if (!res.success || !res.data.datas.length) {
        throw new Error();
      }
      scripts.value = res.data.datas;
    } catch {
      scripts.value = [];
    }
  };

  /**
   * @summary 폴링용 스크립트 상태 동기화
   */
  const pollScriptStatuses = async () => {
    if (!pollingScriptString.value) {
      return;
    }

    try {
      const res = await fetchScriptState({ chatIdToLanguageCodes: pollingScriptString.value });

      if (!res.success) {
        throw new Error();
      }

      res.data.forEach(({ chatId, status, durationInMinutes }) => {
        updateScript(chatId, { status, durationInMinutes });
      });
    } catch (e) {
      console.error(e);
    }
  };

  const startPolling = async () => {
    await pollScriptStatuses();
    pollingIntervalId = setTimeout(startPolling, 10000);
  };

  const stopPolling = () => {
    if (pollingIntervalId) {
      clearTimeout(pollingIntervalId);
      pollingIntervalId = null;
    }
  };

  return {
    scripts,
    scriptSearchForm,
    isLoading,
    limitDateRange,
    searchScripts,
    updateScript,
    pollScriptStatuses,
    startPolling,
    stopPolling,
  };
}
